import { useInfiniteQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { ConnectionsService, SourceTypeEnum } from '../../../../../js/api';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { Drawer, DrawerBody } from '../../../components/drawer';
import { Heading } from '../../../components/heading';
import { ListErrorState } from '../../../components/list-error-state';
import { ListFilter } from '../../../components/list-filter';
import { QueueList } from '../../../components/queue-list';
import { StatusPill } from '../../../components/StatusPill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSortHeader,
} from '../../../components/table';
import { QueueCodes, WorkflowScopes } from '../../../constants/workflows';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useListFilterParams } from '../../../hooks/useListFilterParams';
import { SOURCE_TYPE_ORGANIZATION_BADGE_STYLE } from '../../../utils/dataStyles';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { STICKY_HEADER } from '../../../utils/globalStyles';
import { MAIN_CONTENT_AREA, PAGE_HEADER } from '../../../utils/layoutStyles';
import { TRANSITION_ALL } from '../../../utils/transitionStyles';
import { ConnectionDrawer } from './ConnectionDrawer';
import ConnectionsCard from './ConnectionsCard';
import { ConnectionsHowItWorksDialog } from './ConnectionsHowItWorksDialog';
import { PROVIDER_CONNECTIONS_LIST_QUERY_KEY } from './queryKeys';

const DATE_FORMAT = 'LLL d, yyyy';
const PAGE_SIZE = 20; // Number of items per page

const QUEUE_CATEGORY = 'ProviderConnectionQueueNames';

// Map Code Def category names to their corresponding API parameter names
// This mapping must be kept in sync with the API method signature
// See ConnectionsService.connectionsConnectedFacilityList for available parameters
// Categories are automatically derived from this mapping's keys
type ApiParams = Parameters<typeof ConnectionsService.connectionsConnectedFacilityList>[0];
const CATEGORY_TO_API_PARAM: Record<string, keyof ApiParams> = {
  // Specialty: 'specialties',
  // Add more mappings here as needed:
  // ProviderRateFields: 'rates',
  // PostalStateChoices: 'state',
};

// Derive categories list from the mapping keys - ensures they stay in sync
const CATEGORIES = Object.keys(CATEGORY_TO_API_PARAM) as readonly string[];

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ProviderConnectionsList: React.FC = () => {
  useDocumentTitle('Connections');
  const [searchParams, setSearchParams] = useSearchParams();
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Get the selected queue code from URL params
  // QueueList component handles determining initial tab and validation
  const selectedQueueCode: string | undefined = searchParams.get('queue') || undefined;

  // Use hook to parse and manage filter params
  // TODO: using a hook to centralize query param parsing logic to keep boilerplate out of parent
  // there is an argument for moving this logic to the ListFilter component itself,
  // but the parent component will still need the individual query param values for API calls
  const displayTypePrefKey = 'ProviderConnections';
  const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
    categories: CATEGORIES,
    displayTypePrefKey,
  });

  const search = filterValues.search || '';
  const statuses = filterValues.statuses || [];
  const reasons = filterValues.reasons || [];
  const categoryFilters = useMemo(() => filterValues.categories || {}, [filterValues.categories]);

  // Display type is resolved by the hook (single source of truth)
  const displayType = (filterValues.displayType || 'table') as 'card' | 'table';
  const ordering = (filterValues.ordering || '-updated_at') as string;

  const setOrdering = useCallback(
    (next: string) => {
      updateFilters({
        search: filterValues.search,
        statuses: filterValues.statuses,
        reasons: filterValues.reasons,
        categories: filterValues.categories,
        displayType,
        ordering: next,
      });
    },
    [
      updateFilters,
      filterValues.search,
      filterValues.statuses,
      filterValues.reasons,
      filterValues.categories,
      displayType,
    ]
  );

  // Handle queue change - update URL param
  const handleQueueChange = (queueCode: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (queueCode) {
      newSearchParams.set('queue', queueCode);
    } else {
      newSearchParams.delete('queue');
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  // Get the queue code to pass to API
  // Validation is handled by the backend, invalid queue values will be rejected by django
  // Type assertion needed for TypeScript, but runtime validation happens on backend
  const apiQueueCode = selectedQueueCode ? (selectedQueueCode as ApiParams['queue']) : undefined;

  // Build API parameters from category filters using CATEGORY_TO_API_PARAM mapping
  const categoryApiParams = useMemo(() => {
    const params: Partial<ApiParams> = {};
    Object.entries(categoryFilters).forEach(([category, values]) => {
      const apiParamName = CATEGORY_TO_API_PARAM[category];
      if (apiParamName && values.length > 0) {
        // Type assertion needed because TypeScript can't infer types from dynamic property access
        // The mapping is validated at compile-time through CATEGORY_TO_API_PARAM's Record type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (params as Record<string, unknown>)[apiParamName] = values;
      }
    });
    return params;
  }, [categoryFilters]);

  // Build query key with filters
  const queryKey = useMemo(
    () => [
      PROVIDER_CONNECTIONS_LIST_QUERY_KEY,
      selectedQueueCode,
      search,
      statuses,
      reasons,
      categoryFilters,
      ordering,
    ],
    [selectedQueueCode, search, statuses, reasons, categoryFilters, ordering]
  );

  // Fetch connections filtered by selected queue and filters
  // Note: The API automatically filters by provider_id via security context headers (SIH-Context-ID)
  // No need to explicitly pass provider parameter
  const {
    data,
    isLoading: isLoadingConnections,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      return ConnectionsService.connectionsConnectedFacilityList({
        queue: apiQueueCode,
        search: search || undefined,
        status: statuses.length > 0 ? (statuses as Array<string | null>) : undefined,
        reason: reasons.length > 0 ? (reasons as Array<string | null>) : undefined,
        ...categoryApiParams,
        ordering: ordering as ApiParams['ordering'],
        limit: PAGE_SIZE,
        offset: pageParam as number,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.next) {
        return allPages.length * PAGE_SIZE;
      }
      return undefined;
    },
    enabled: !!selectedQueueCode, // Only fetch when we have a queue selected
  });

  const connections = useMemo(() => {
    return data?.pages.flatMap((page) => page.results || []) || [];
  }, [data]);

  const totalCount = data?.pages[0]?.count || 0;

  // Set up infinite scroll
  const loadMoreConnections = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMoreConnections, hasNextPage || false, {
    enabled: hasNextPage && !isLoadingConnections,
  });

  // Check URL for connection ID and open drawer if present
  useEffect(() => {
    const connectionIdFromUrl = searchParams.get('id');
    if (connectionIdFromUrl) {
      setIsDrawerOpen(true);
    } else {
      // URL param was removed, close drawer only if it was open
      if (isDrawerOpen) {
        setIsDrawerOpen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleRowClick = useCallback(
    (connectionId: string) => {
      setIsDrawerOpen(true);
      // Update URL to include connection ID
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('id', connectionId);
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    // Remove connection ID from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('id');
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Use URL param as single source of truth for selection to avoid state desync
  const selectedIdFromUrl = searchParams.get('id');

  // Determine index of selected item in the current results
  const selectedIndex = useMemo(() => {
    if (!selectedIdFromUrl) return -1;
    // Normalize both sides to string for robust comparison
    return connections.findIndex((c) => String(c.id) === String(selectedIdFromUrl));
  }, [connections, selectedIdFromUrl]);

  const prevConnectionId: string | null = useMemo(() => {
    if (selectedIndex > 0) return connections[selectedIndex - 1]?.id ?? null;
    return null;
  }, [connections, selectedIndex]);

  const nextConnectionId: string | null = useMemo(() => {
    if (selectedIndex >= 0 && selectedIndex < connections.length - 1)
      return connections[selectedIndex + 1]?.id ?? null;
    return null;
  }, [connections, selectedIndex]);

  // Cache last-known neighbors so if the selected item drops from the list, we can still advance
  const neighborsByIdRef = useRef<Map<string, { prev: string | null; next: string | null }>>(
    new Map()
  );

  // Update neighbor cache whenever the selected item is present in the list
  useEffect(() => {
    if (!selectedIdFromUrl) return;
    if (selectedIndex >= 0) {
      neighborsByIdRef.current.set(String(selectedIdFromUrl), {
        prev: prevConnectionId ?? null,
        next: nextConnectionId ?? null,
      });
    }
  }, [selectedIdFromUrl, selectedIndex, prevConnectionId, nextConnectionId]);

  // Deep-link guard for initial `id` from URL: do not auto-advance/clear while the
  // currently selected id is exactly the initially deep-linked id. The drawer can fetch
  // the item by id independently even if it's not present on the first page.
  const initialIdRef = useRef<string | null>(searchParams.get('id'));

  // Fallback auto-advance: if the selected id is no longer in the list for the current queue,
  // navigate to cached next/prev, or close if none. Skips when on the initial deep-linked id.
  useEffect(() => {
    // Only auto-advance when a specific queue (not 'all') is selected
    if (!selectedQueueCode || selectedQueueCode === 'all') return;
    if (!selectedIdFromUrl) return;

    // Keep the deep-linked id stable on initial load
    if (initialIdRef.current && selectedIdFromUrl === initialIdRef.current) {
      return;
    }

    // When the list finishes (re)loading and no longer contains the selected item
    if (selectedIndex === -1 && !isLoadingConnections) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('id');
      nextParams.delete('tab');
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    selectedQueueCode,
    selectedIdFromUrl,
    selectedIndex,
    isLoadingConnections,
    searchParams,
    setSearchParams,
  ]);

  return (
    <>
      <div className="tw:flex tw:h-[calc(100vh-72px)] tw:overflow-hidden">
        {/* Main content area - takes 2/3 when drawer open, full width when closed */}
        <div
          className={clsx(
            `tw:flex tw:flex-col ${TRANSITION_ALL} tw:overflow-hidden`,
            isDrawerOpen ? 'tw:w-3/5 tw:lg:w-3/5' : 'tw:w-full'
          )}
        >
          <main className={MAIN_CONTENT_AREA}>
            <div className={STICKY_HEADER}>
              <div
                className={twMerge(
                  clsx(PAGE_HEADER, 'tw:flex tw:items-center tw:gap-3 tw:justify-start')
                )}
              >
                <Heading level={1} className="tw:!text-brand-green-0 tw:!mb-0">
                  <i className="far fa-fw fa-handshake tw:mr-1" />
                  Connections
                </Heading>
                <button
                  type="button"
                  onClick={() => setHowItWorksOpen(true)}
                  className="tw:!rounded-full tw:bg-blue-200 tw:text-blue-800 tw:border-0 tw:py-1 tw:px-3 tw:cursor-pointer tw:font-medium tw:!text-sm tw:shrink-0"
                >
                  <i className="far fa-info-circle tw:mr-1" />
                  How it works
                </button>
              </div>

              <div className="tw:mt-4">
                <QueueList
                  categoryName={QUEUE_CATEGORY}
                  notificationScope={WorkflowScopes.ConnectedFacility}
                  selectedQueue={selectedQueueCode || undefined}
                  priorityQueues={true}
                  priorityQueueList={[QueueCodes.NEEDS_RESPONSE]}
                  onQueueChange={handleQueueChange}
                />
              </div>
            </div>
            <div className="tw:mt-1">
              <div className="tw:group" data-drawer-open={isDrawerOpen ? 'true' : 'false'}>
                <ListFilter
                  show_search={true}
                  workflow_scope={WorkflowScopes.ConnectedFacility}
                  categories={Array.from(CATEGORIES)}
                  showStatus={true}
                  showReason={false}
                  showDisplayType={true}
                  values={filterValues}
                  onFilterChange={updateFilters}
                  sortOptions={[
                    { label: 'Last Updated', value: 'updated_at' },
                    { label: 'Created', value: 'connected_at' },
                    { label: 'Facility', value: 'facility__name' },
                    { label: 'Location', value: 'facility__city' },
                    { label: 'Status', value: 'current_status__title' },
                  ]}
                />
              </div>
            </div>

            <div className="tw:mt-6">
              {isError ? (
                <ListErrorState testId="connections-error" />
              ) : isLoadingConnections && connections.length === 0 ? (
                <div className="tw:text-center tw:py-8">Loading connections...</div>
              ) : connections.length === 0 ? (
                <div className="tw:text-center tw:text-zinc-500 tw:py-8">
                  No connections found
                  <div className="tw:italic tw:text-zinc-500 tw:text-sm">
                    Refine search criteria or check back later
                  </div>
                </div>
              ) : (
                <>
                  {/* Show total count when there are results */}
                  {connections.length > 0 && (
                    <div className="tw:text-right tw:text-xs tw:text-zinc-500 tw:my-1 tw:mr-1">
                      Showing {connections.length} of {totalCount} connections
                    </div>
                  )}
                  {displayType === 'card' ? (
                    <div className="tw:@container">
                      <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:@min-[513px]:grid-cols-2 tw:@min-[793px]:grid-cols-3">
                        {connections.map((connection) => {
                          // Check if this connection is selected
                          const connectionIdFromUrl = searchParams.get('id');
                          const isSelected = connection.id === connectionIdFromUrl;

                          return (
                            <button
                              key={connection.id}
                              type="button"
                              onClick={() => handleRowClick(connection.id)}
                              className={clsx('tw:cursor-pointer tw:block tw:w-full tw:text-left')}
                            >
                              <ConnectionsCard connection={connection} isSelected={isSelected} />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <>
                      <Table striped dense>
                        <TableHead>
                          <TableSortHeader
                            sortKey="facility__name"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Facility Name
                          </TableSortHeader>
                          <TableSortHeader
                            sortKey="facility__city"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Location
                          </TableSortHeader>
                          <TableSortHeader
                            sortKey="healthcare_entity__name"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Organization
                          </TableSortHeader>
                          <TableSortHeader
                            sortKey="current_status__title"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Status
                          </TableSortHeader>
                          {selectedQueueCode === 'all' && <TableHeader>Reason</TableHeader>}
                          <TableSortHeader
                            sortKey="updated_at"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Last Updated
                          </TableSortHeader>
                          <TableSortHeader
                            sortKey="connected_at"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Created
                          </TableSortHeader>
                        </TableHead>
                        <TableBody>
                          {connections.map((connection) => {
                            // Check if this connection is selected
                            const connectionIdFromUrl = searchParams.get('id');
                            const isSelected = connection.id === connectionIdFromUrl;

                            return (
                              <TableRow
                                key={connection.id}
                                onClick={() => handleRowClick(connection.id)}
                                className={clsx(isSelected ? 'tw:!bg-brand-green-2' : '')}
                              >
                                <TableCell>
                                  {connection.facility.name}
                                  {connection.source_type === SourceTypeEnum.ORGANIZATION && (
                                    <span
                                      className={clsx(
                                        'tw:ml-2',
                                        SOURCE_TYPE_ORGANIZATION_BADGE_STYLE
                                      )}
                                    >
                                      1099
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {getFacilityLocationDisplayValue(connection.facility) || '--'}
                                </TableCell>
                                <TableCell>{connection.healthcare_entity.name}</TableCell>
                                <TableCell>
                                  <StatusPill
                                    scope={WorkflowScopes.ConnectedFacility}
                                    status={connection.current_status}
                                  />
                                </TableCell>
                                {selectedQueueCode === 'all' && (
                                  <TableCell>{connection.current_reason?.title || '--'}</TableCell>
                                )}
                                <TableCell>
                                  {connection.updated_at
                                    ? format(parseISO(connection.updated_at), DATE_FORMAT)
                                    : '-'}
                                </TableCell>
                                <TableCell>
                                  {connection.connected_at
                                    ? format(parseISO(connection.connected_at), DATE_FORMAT)
                                    : '-'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </>
                  )}

                  {/* Infinite scroll loading indicator */}
                  {hasNextPage && (
                    <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                      {isFetchingNextPage && (
                        <div className="tw:text-center tw:text-zinc-500">
                          Loading more connections...
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>

        {/* Drawer - side-by-side on desktop, modal on mobile */}
        <Drawer open={isDrawerOpen} onClose={handleCloseDrawer} width="3xl" mode="side-by-side">
          <DrawerBody className="tw:!text-sm/6">
            {selectedIdFromUrl && (
              <ConnectionDrawer
                key={selectedIdFromUrl}
                connectionId={selectedIdFromUrl}
                onClose={handleCloseDrawer}
              />
            )}
          </DrawerBody>
        </Drawer>
      </div>

      <ConnectionsHowItWorksDialog open={howItWorksOpen} onClose={() => setHowItWorksOpen(false)} />
    </>
  );
};
